import {
  type ActiveElement,
  BarController,
  BarElement,
  Chart,
  type ChartEvent,
  Interaction,
  type InteractionModeFunction,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import classNames from "classnames";
import * as React from "react";

import {
  FALLBACK_COLOR,
  resolveHoverColors,
  resolveSeriesColors,
  seriesColorVars,
} from "@/shared/charts";
import { SeriesColor } from "@/shared/domain/dashboards/DashboardDefinition";
import {
  ChartDisplayType,
  type GroupTruncation,
} from "@/shared/domain/metrics";
import { Button } from "@/shared/foundation/primitives";
import { nsToDateLabel } from "@/shared/time/time";

import { GROUP_CAP_EXPLANATION, hiddenGroupsText } from "./groupCap";
import { hoverBandPlugin } from "./hoverBand";
import styles from "./MetricChart.module.css";

// Register only the Chart.js pieces this chart uses (matches the explicit
// registration style in PlotRenderer.ts). Registration is global + idempotent.
Chart.register(
  BarController,
  BarElement,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
);

// NOT registered globally with the rest. Registration is process-wide, and the
// visualizer's plot panel draws Chart.js charts of its own with their own
// pan/zoom; a globally registered zoom plugin would attach itself — and a
// Hammer manager — to those too, inert by configuration but listening all the
// same. It goes on this chart's `plugins` array instead, where it reaches
// exactly the charts that asked for it.

/**
 * WHY THE BUCKETED AXIS IS NUMERIC RATHER THAN A CATEGORY SCALE.
 *
 * It used to be `CategoryScale`, which was the right call while these charts
 * only ever sat still: gap-filled buckets are uniform, so a category axis's
 * even spacing IS time-proportional, and it renders one tick per bucket for
 * free.
 *
 * It cannot pan, though. A category scale's bounds are label INDICES —
 * `buildTicks` slices the label array with them and walks `for (value = min;
 * value <= max; value++)` — so fractional bounds produce ticks with no labels.
 * Its position is therefore quantised to whole buckets, and so is any pan of
 * it: the plot cannot move until the reader has dragged a full bucket's width
 * and then it jumps the lot. Zoomed into four monthly buckets on an 866px
 * plot, that is 217px of dead drag followed by a 217px lurch. (The plugin's
 * own step made it worse still — `scaleLength / max(range, 10)` fires every
 * 87px, so the plot travelled two and a half times as far as the hand holding
 * it — but fixing the rate only trades a lurch for a heavier one.)
 *
 * So the bucketed axis is a LINEAR scale positioned by bucket index. Every
 * pixel is a valid position, which is what makes the drag continuous, and
 * index positions are exactly the even spacing the category scale gave. What
 * the category scale did implicitly this now says out loud: the half-bucket
 * end padding (see {@link bucketBounds}) and the per-bucket tick text (see
 * {@link chartLabels}), which a callback maps back from index to label.
 */

export interface PlotDatum {
  /** Bucket start, Unix-epoch nanoseconds. `bigint` to keep full ns precision:
   *  on the category x-axis Chart.js never reads these (it scales off
   *  `label`), they only ride along for the click → drill-down query. */
  start_time: bigint;
  /** Bucket end, Unix-epoch nanoseconds. See `start_time`. */
  end_time: bigint;
  /** Absent on placeholder rows; present on real aggregate buckets. */
  value?: number;
  /** Pre-formatted x-axis label (short ISO date). Category x-axis only. */
  label: string;
  /** Pre-formatted tooltip title (start–end range). */
  tooltipTitle: string;
  /** Linear x-axis only: this point's x coordinate in Unix-epoch
   *  MILLISECONDS. Chart.js scales numerically off this, so points sit at
   *  their real elapsed distance from one another rather than at even
   *  positional intervals. Ignored on the category axis. */
  x?: number;
  /** Raw mode only: the single session this point came from. */
  session_id?: string;
  /** GROUPED mode only: the value of the card's group-by field for this
   *  point's rows, as the backend cast it to text. `null` is the "field
   *  unset" group — a series of its own, not an absent one. Absent entirely
   *  (rather than null) on an ungrouped chart, whose points belong to no
   *  group; that absence is what makes the view draw ONE dataset labelled by
   *  the metric, exactly as before. Carried through to the drill-down, which
   *  filters on it. */
  group_key?: string | null;
  /** GROUPED mode only: what the legend shows for this point's series (see
   *  `valueOrNotSet` — `group_key` is a wire value and may be null). */
  group_label?: string;
}

/**
 * How the x-axis maps a point to a position.
 *
 * `category` — evenly spaced positions labelled by {@link PlotDatum.label}.
 * The AGGREGATED path: its buckets are uniform and gap-filled, so even
 * spacing IS time-proportional.
 *
 * `linear` — numeric epoch-ms positions from {@link PlotDatum.x}. The RAW
 * path: one point per session at whatever moment that session ended, with no
 * uniform bucket to synthesize gaps from. Category spacing would draw a
 * three-year-old session and yesterday's an equal distance apart.
 */
export type ChartXScale = "category" | "linear";

/**
 * The span a LINEAR x-axis is pinned to, in epoch MILLISECONDS — the unit
 * {@link PlotDatum.x} carries.
 *
 * Without one Chart.js scales the axis to its data's own min/max, so a chart
 * whose metric stopped reporting early draws a shorter window than its
 * neighbours over the same selection. Ignored on the category axis, whose
 * extent is its (already gap-filled and padded) label list.
 */
export interface ChartXDomain {
  minMs: number;
  maxMs: number;
}

/**
 * The display this axis can actually draw.
 *
 * Bar needs the CATEGORY axis, whose evenly-spaced slots give every bar the
 * same width. The linear axis a raw series draws on is irregular by
 * construction — one point per session, at whatever moment that session ended
 * — and Chart.js sizes bars from the smallest gap between points, so a single
 * close pair shrinks every bar on the chart to a hairline. A card that asks
 * for one there gets the line it would otherwise be, rather than a plot that
 * looks empty.
 *
 * Coerced HERE, at the one place that knows both the requested display and the
 * axis, rather than in each caller: the picker simply does not offer bar for a
 * linear-axis card, so this only ever catches a display that arrived some
 * other way (a hand-edited definition, or one saved while the card was
 * aggregated).
 */
function drawableDisplay(
  display: ChartDisplayType,
  xScale: ChartXScale,
): ChartDisplayType {
  return display === "bar" && xScale === "linear" ? "line" : display;
}

export interface MetricChartViewProps {
  /** Pre-computed series: gap-filled with formatted labels for the category
   *  axis, or sorted ascending by `x` for the linear one. */
  series: PlotDatum[];
  metricName: string;
  /** Unit of measure for the plotted values; titles the y-axis. Omit if unitless. */
  unit?: string | null;
  /** Line (default), bar, or scatter rendering. Bar is drawn only on the
   *  category axis — Chart.js sizes bars from the smallest inter-point
   *  interval, which on an irregular linear axis collapses them to hairlines —
   *  so a bar asked for on the linear axis falls back to a line (see
   *  {@link drawableDisplay}). Line and scatter draw on either. */
  display?: ChartDisplayType;
  /** Positional (default) or time-proportional x-axis. */
  xScale?: ChartXScale;
  /** Palette slot the first series is drawn in; further series follow on from
   *  it. Omit for the automatic assignment. */
  color?: SeriesColor;
  /** Linear axis only: pin the axis to this span instead of letting Chart.js
   *  fit it to the data. See {@link ChartXDomain}. */
  xDomain?: ChartXDomain;
  /** The field path the series are split by (e.g. `session.custom.build_id`).
   *  Kept for the legend's ACCESSIBLE NAME: a group key is only a VALUE —
   *  `v1.2` does not say which field it is a value of — so without this the
   *  list announces values the reader cannot place. The visible statement of
   *  the grouping belongs to the card's caption line, not here. Omit on an
   *  ungrouped chart. */
  groupBy?: string;
  /** Groups the server's cap left out of `series`, when it left any out: the
   *  legend then ends with a note saying so. Omit when every group is drawn. */
  groupTruncation?: GroupTruncation | null;
  /**
   * Fired when a real (non-placeholder) datum is clicked.
   *
   * `datum` is the point under the cursor — the series actually aimed at, not
   * the first one. `selection` is everything the chart reported for that
   * click: on the linear axis, each group's nearest point (see
   * {@link NEAREST_PER_GROUP}); elsewhere, the whole hovered index. It is what
   * the tooltip listed, so a caller that drills into `selection` shows exactly
   * the rows the tooltip named. `datum` is always its first entry.
   */
  onDatumClick?: (datum: PlotDatum, selection: PlotDatum[]) => void;
}

/**
 * The dataset's `data` array for `xScale`, `{x, y}` pairs either way.
 *
 * Bucketed: `x` is the point's INDEX in the gap-filled run — the same even
 * spacing a category axis drew it at, now stated as a number so the axis can
 * be a continuous one (see the note above {@link PlotDatum}).
 *
 * Time: `x` is epoch ms, so points sit at their real elapsed distance from one
 * another rather than at even intervals.
 *
 * A datum with no value plots as `NaN`: `spanGaps` bridges it for a line, and
 * for bars `skipNull` steps over it, treating a NaN exactly as it treats a
 * null (`isNullOrUndef(val) || isNaN(val)`). On the time axis it is the
 * type-level floor rather than a case the raw series produces.
 */
function datasetData(
  series: PlotDatum[],
  xScale: ChartXScale,
): { x: number; y: number }[] {
  return xScale === "linear"
    ? series.map((d) => ({ x: d.x ?? Number.NaN, y: d.value ?? Number.NaN }))
    : series.map((d, i) => ({ x: i, y: d.value ?? Number.NaN }));
}

/**
 * Bounds for the bucketed x-axis, in the index space {@link datasetData} puts
 * its points in: half a bucket of padding either side, so the first and last
 * bucket get a full band rather than being clipped at the plot's edge.
 *
 * This is the half-step a CATEGORY scale adds for you through `offset`. The
 * bucketed axis is a numeric scale that merely happens to be positioned by
 * index, so it states the padding itself — see {@link MetricChartView} for why
 * it is numeric at all.
 */
function bucketBounds(bucketCount: number): { min: number; max: number } {
  return { min: -0.5, max: Math.max(bucketCount - 0.5, 0.5) };
}

/**
 * The tick text for a position on the bucketed axis: that bucket's own label.
 *
 * Blank for anything that does not name a bucket. `afterBuildTicks` already
 * offers whole buckets only, so this is the floor rather than a case seen in
 * practice — but the axis is continuous and its ends are padded, so a position
 * off the run is representable, and a date drawn there would name a bucket the
 * chart does not show.
 */
function bucketTickLabel(value: number, labels: string[]): string {
  return Number.isInteger(value) ? (labels[value] ?? "") : "";
}

/**
 * Switch the tooltip off for the duration of a pan or a zoom, and back on
 * once the gesture settles.
 *
 * A tooltip is a reading of one moment, and it has no business being on screen
 * while the reader is moving the plot around. Left alone it is actively in the
 * way: it stays painted from whatever the last hover made active, it re-raises
 * itself on every pointer move through the gesture, and because it is drawn
 * over the middle of the plot it covers the very part being dragged into view.
 *
 * Clearing the active elements alone does not do it — the next mouse move
 * simply resolves a new reading — so this flips `enabled`, which is what
 * `Tooltip.draw` actually gates on, and clears the active elements too. The
 * clearing is what stops a reading of wherever the pointer happened to be when
 * the gesture began from snapping back the instant it ends; the reader's next
 * move raises a fresh one, on what they have actually come to rest over.
 */
function setTooltipEnabled(chart: Chart, enabled: boolean) {
  const tooltip = chart.options.plugins?.tooltip;
  if (tooltip) {
    tooltip.enabled = enabled;
  }
  chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
}

/** The bucketed axis's tick text, one per bucket, or `undefined` on the time
 *  axis (which reads its positions off the data and formats its own ticks).
 *
 * Taken from the FIRST partition: every series is gap-filled over the union of
 * buckets (see `toGroupedSeries`), so they all carry the same labels in the
 * same order — which is what lets them share one axis, and what makes a
 * point's index mean the same thing in every series. */
function chartLabels(
  partitions: SeriesPartition[],
  xScale: ChartXScale,
): string[] | undefined {
  if (xScale === "linear") {
    return undefined;
  }
  return (partitions[0]?.points ?? []).map((d) => d.label);
}

/**
 * A name for the WINDOW this chart covers, so a change of window can be told
 * from a change of data within one.
 *
 * The distinction is what makes zooming usable. A reader's zoom is a way of
 * looking into the window the card was drawn for, so it has to survive the
 * card refetching (a raw card's "Load all" drains pages into the series under
 * their feet) while still giving way when the dashboard is re-scoped to a
 * different span — at which point the zoomed-into range names time the card no
 * longer draws.
 *
 * The linear axis states its window outright as {@link ChartXDomain}. The
 * category axis has none: its window IS its bucket run, so the run's length
 * and its two ends stand in for one. A sliding range refetched a day later
 * keeps its length and moves both ends, which is correctly a different window.
 */
function xExtentKey(
  xScale: ChartXScale,
  xDomain: ChartXDomain | undefined,
  partitions: SeriesPartition[],
): string {
  if (xScale === "linear") {
    return `linear:${String(xDomain?.minMs)}:${String(xDomain?.maxMs)}`;
  }
  const labels = chartLabels(partitions, xScale) ?? [];
  return `category:${String(labels.length)}:${labels[0] ?? ""}:${labels[labels.length - 1] ?? ""}`;
}

/**
 * Whether the x-axis is showing less than the whole window it was given.
 *
 * Asked of the x-axis ALONE, unlike the plugin's own `isZoomedOrPanned()`,
 * which asks every scale. The y-axis is not zoomable here (both modes are
 * `"x"`) but it does rescale itself to whatever data is in view, and the
 * plugin only re-reads a scale's "original" bounds when that scale's own
 * min/max OPTIONS change — which for y is never. So y drifts away from its
 * recorded original and never comes back, and the plugin's answer latches on
 * to "zoomed" for the life of the chart.
 */
function isXZoomed(chart: Chart): boolean {
  const original = chart.getInitialScaleBounds().x;
  const scale = chart.scales.x;
  if (original === undefined || scale === undefined) {
    return false;
  }
  return (
    (original.min !== undefined && scale.min !== original.min) ||
    (original.max !== undefined && scale.max !== original.max)
  );
}

/** Chart.js interaction mode name for {@link nearestPerGroup}. Declared on
 *  Chart.js's own mode map so `interaction.mode` accepts it by name. */
declare module "chart.js" {
  interface InteractionModeMap {
    nearestPerGroup: InteractionModeFunction;
  }
}

const NEAREST_PER_GROUP = "nearestPerGroup";

/**
 * How far from the cursor (px) a group's nearest point may sit and still be
 * reported.
 *
 * Without a cutoff every group always contributes, so a series that stopped
 * reporting in March would answer a hover in September with its last point —
 * a reading the chart does not show anywhere near the cursor. Roughly the
 * width of a point plus its neighbour's, which is the neighbourhood a reader
 * takes a crosshair to be asking about.
 */
const NEAREST_GROUP_RADIUS_PX = 24;

/**
 * Each group's point nearest the cursor's x, at most one per dataset.
 *
 * Chart.js's own `index` mode reports the same ARRAY INDEX from every dataset,
 * which is only the same moment when every series carries the same buckets.
 * The aggregated path guarantees that by gap-filling; the raw path
 * deliberately does not (`toRawSeries`), so there index `i` is a different
 * session at a different time in each group and `index` mode pairs up readings
 * that never coexisted. This asks the question that actually makes sense on a
 * time axis — "what was each group doing HERE" — and answers it with real
 * points rather than synthesized ones.
 */
function nearestPerGroup(
  chart: Chart,
  event: ChartEvent,
  _options: unknown,
  useFinalPosition?: boolean,
): ActiveElement[] {
  const cursorX = event.x;
  if (cursorX === null || cursorX === undefined) {
    return [];
  }
  const hits: { element: ActiveElement; distance: number }[] = [];
  for (const meta of chart.getSortedVisibleDatasetMetas()) {
    let best: { index: number; distance: number } | undefined;
    for (const [index, element] of meta.data.entries()) {
      const { x } = element.getProps(["x"], useFinalPosition);
      const distance = Math.abs(x - cursorX);
      if (best === undefined || distance < best.distance) {
        best = { index, distance };
      }
    }
    if (best !== undefined && best.distance <= NEAREST_GROUP_RADIUS_PX) {
      hits.push({
        element: {
          datasetIndex: meta.index,
          index: best.index,
          element: meta.data[best.index],
        },
        distance: best.distance,
      });
    }
  }
  // Nearest first, so `elements[0]` is the point actually aimed at — which is
  // what makes the caller's "clicked datum" the series under the cursor
  // without asking Chart.js a second question.
  hits.sort((a, b) => a.distance - b.distance);
  return hits.map((hit) => hit.element);
}

Interaction.modes[NEAREST_PER_GROUP] = nearestPerGroup;

/**
 * The groups the reader has switched off, by {@link SeriesPartition.key}.
 *
 * Keyed by GROUP, not by index. Series are ordered by group key
 * (`toGroupedSeries`), so a group appearing in a later refetch shifts every
 * index after it — an index-keyed set would silently move the reader's choice
 * onto a different device. A key no longer present simply matches nothing,
 * which is what makes a hidden series survive a regrouping and come back with
 * its group.
 */
type HiddenSeriesKeys = ReadonlySet<string | null | undefined>;

/** One plotted line: the group it stands for and its points. */
interface SeriesPartition {
  /** The group's wire value, `null` for the "no value" group, and
   *  `undefined` on an UNGROUPED chart — whose one line belongs to no group
   *  and is labelled by the metric instead. */
  key: string | null | undefined;
  /** Legend text, or `undefined` on an ungrouped chart. */
  label: string | undefined;
  points: PlotDatum[];
}

/**
 * Split a flat series into one partition per `group_key`, ordered by key with
 * the null ("Not set") group last — the order `toGroupedSeries` gives the
 * aggregated path.
 *
 * Ordered by key rather than by first appearance because a raw card's points
 * arrive in time order, page by page: a page loaded later can bring in a group
 * whose first point is earlier than any other's, and first-appearance order
 * would then shift every series after it into another palette slot.
 *
 * A series whose points carry no `group_key` at all — every ungrouped chart,
 * and every raw one — yields exactly ONE partition, which is what keeps those
 * paths drawing the single unlabelled line they always drew.
 */
function partitionSeries(series: PlotDatum[]): SeriesPartition[] {
  const byKey = new Map<string | null | undefined, SeriesPartition>();
  for (const datum of series) {
    const key = datum.group_key;
    let partition = byKey.get(key);
    if (partition === undefined) {
      partition = { key, label: datum.group_label, points: [] };
      byKey.set(key, partition);
    }
    partition.points.push(datum);
  }
  return [...byKey.values()].sort((a, b) => compareGroupKeys(a.key, b.key));
}

/** Null (the "Not set" group) sorts last; the rest compare as text, as
 *  `toGroupedSeries` orders them. */
function compareGroupKeys(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  if (a === b) {
    return 0;
  }
  if (a === null || a === undefined) {
    return 1;
  }
  if (b === null || b === undefined) {
    return -1;
  }
  return a.localeCompare(b, "en");
}

/**
 * One Chart.js dataset per partition, labelled and coloured — at rest and
 * hovered.
 *
 * The hover colours are stated rather than left to Chart.js, which derives its
 * own from `backgroundColor` but cannot parse the colour space the series
 * palette computes to; see {@link resolveHoverColors}. Stating them keeps one
 * highlight treatment across every series and both chart types, so a grouped
 * chart answers the pointer the way an ungrouped one always has.
 */
function buildDatasets(
  partitions: SeriesPartition[],
  xScale: ChartXScale,
  metricName: string,
  canvas: HTMLCanvasElement,
  hiddenKeys: HiddenSeriesKeys,
  display: ChartDisplayType,
  colorOverride?: SeriesColor,
) {
  const colors = resolveSeriesColors(canvas, partitions.length, colorOverride);
  const hoverColors = resolveHoverColors(canvas, colors);
  return partitions.map((partition, i) => ({
    label: partition.label ?? metricName,
    // The build-time half of the visibility story; the update effect asserts
    // it again through `setDatasetVisibility`, which is what Chart.js treats
    // as authoritative once a dataset's metadata exists.
    hidden: hiddenKeys.has(partition.key),
    data: datasetData(partition.points, xScale),
    borderColor: colors[i] ?? FALLBACK_COLOR,
    backgroundColor: colors[i] ?? FALLBACK_COLOR,
    hoverBackgroundColor: hoverColors[i] ?? FALLBACK_COLOR,
    hoverBorderColor: hoverColors[i] ?? FALLBACK_COLOR,
    // Connect across missing buckets (rendered as null) instead of leaving
    // gaps; the gap-fill still keeps the x-axis spacing time-proportional.
    spanGaps: true,
    // BARS ONLY (the line controller ignores it). A group the backend never
    // reported in a bucket reaches Chart.js as a gap — the aggregate response
    // is sparse, and `toGroupedSeries` densifies it so every series can share
    // one axis. By default Chart.js reserves that silent group its share of
    // the bucket's band anyway, so a bucket only one of twelve devices
    // reported drew that device's bar at a twelfth of the slot with eleven
    // blank shares beside it — and widening the dashboard's range only made it
    // worse, since the group list is uncapped and every group that enters
    // thins every bar again. `skipNull` divides each bucket among the groups
    // that actually reported it. (It reads a `NaN` as a gap exactly as it
    // reads a `null` — `isNullOrUndef(val) || isNaN(val)` — which is what lets
    // `datasetData` carry the empties as NaN.)
    //
    // The cost is that a group's offset WITHIN a bucket now depends on which
    // others reported there: colour and the legend identify a series, position
    // no longer does. Deliberate — a bar too thin to see identifies nothing at
    // all. The gaps stay in `data`, so a hit's index is still its axis index,
    // which is what the click → drill-down reads it back by.
    skipNull: true,
    // A scatter IS the line controller with the connecting line switched off,
    // rather than Chart.js's own `scatter` type. Reusing the line controller
    // is what lets one display work on both axes, and keeps the points, hover
    // colours and interaction modes identical to the line chart's. (It was
    // also once the only option: the scatter controller insists on `{x, y}`
    // data and a numeric x-axis, which the bucketed path did not have until
    // that axis stopped being a category scale.) Ignored by the bar
    // controller.
    showLine: display !== "scatter",
  }));
}

/**
 * Chart presentation layer for metrics.
 *
 * Renders a Chart.js canvas from an already-computed {@link PlotDatum} series,
 * or an empty-state message when the series is empty.
 *
 * Does no data fetching or transformation — that belongs in the {@link MetricChart} wrapper.
 */
export function MetricChartView({
  series,
  metricName,
  unit = null,
  display = "line",
  xScale = "category",
  xDomain,
  color: seriesColor,
  groupBy,
  groupTruncation,
  onDatumClick,
}: MetricChartViewProps) {
  const chartRef = React.useRef<Chart | null>(null);

  // The chart is created once per `display` type and then mutated in place (see
  // the update effect below) as the series / metric name change, so it — and
  // its option callbacks — outlive those data changes. The callbacks therefore
  // read the latest values through refs instead of closing over stale ones.
  const clickRef = React.useRef(onDatumClick);
  clickRef.current = onDatumClick;
  // One entry per plotted line. Recomputed on every render (it is a linear
  // pass over the series the parent already memoized) and held in a ref for
  // the same reason the other option-callback inputs are: the chart outlives
  // the data.
  const partitions = partitionSeries(series);
  const partitionsRef = React.useRef(partitions);
  partitionsRef.current = partitions;
  const metricNameRef = React.useRef(metricName);
  metricNameRef.current = metricName;
  const unitRef = React.useRef(unit);
  unitRef.current = unit;
  // Read at build time and reassigned by the update effect below, for the same
  // reason the other option inputs are held in refs: the chart outlives the
  // data, and `setCanvas` must not be rebuilt when only the domain moves.
  const xDomainRef = React.useRef(xDomain);
  xDomainRef.current = xDomain;
  // The bucketed axis's tick text, by index. Held in a ref for the same reason
  // as the rest: the tick callback is baked into the options at build time and
  // outlives every refetch that changes the buckets under it.
  const labels = chartLabels(partitions, xScale) ?? [];
  const labelsRef = React.useRef(labels);
  labelsRef.current = labels;

  // Whether the reader has zoomed or panned away from the full window. Drives
  // nothing but the reset control's presence — the zoom itself lives on the
  // scale, where the plugin puts it.
  const [zoomed, setZoomed] = React.useState(false);
  // The window the chart was last put on (see {@link xExtentKey}). Compared on
  // every data update to tell a refetch from a re-scoped dashboard, which is
  // the difference between keeping a reader's zoom and dropping it.
  const xExtentRef = React.useRef("");

  const resetView = React.useCallback((event: React.MouseEvent) => {
    // The control sits over the plot, so its click is stopped here rather than
    // left to bubble into anything the card wraps the chart in — the same
    // guard the plot panel's own reset puts on.
    event.stopPropagation();
    chartRef.current?.resetZoom("none");
    setZoomed(false);
  }, []);

  // Which series the reader has switched off through the legend. Local to the
  // view: it is a way of looking at this chart, not a property of the card's
  // configuration, so it is deliberately not persisted or lifted — and it
  // outlives a refetch, which is the point (see {@link HiddenSeriesKeys}).
  const [hiddenKeys, setHiddenKeys] = React.useState<HiddenSeriesKeys>(
    () => new Set(),
  );
  const hiddenKeysRef = React.useRef(hiddenKeys);
  hiddenKeysRef.current = hiddenKeys;

  const toggleSeries = React.useCallback((key: string | null | undefined) => {
    setHiddenKeys((current) => {
      const next = new Set(current);
      // `delete` reports whether the key was there, so one call both tests
      // and un-hides.
      if (!next.delete(key)) {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Build (and tear down) the chart as the <canvas> mounts and whenever
  // `display` or `xScale` changes — Chart.js can't switch an existing chart's
  // type or a scale's type, so those are the cases that need a fresh instance.
  // (A line ⇄ scatter switch keeps the same controller and would survive an
  // update alone, but it rides along here rather than being special-cased.)
  // Series / metric name changes never reach here; they flow through
  // chart.update() below. The canvas is absent in the empty state, so this
  // also disposes the chart when the series empties out.
  const setCanvas = React.useCallback(
    (canvas: HTMLCanvasElement | null) => {
      chartRef.current?.destroy();
      chartRef.current = null;
      if (!canvas) {
        return;
      }

      const drawn = drawableDisplay(display, xScale);
      chartRef.current = new Chart(canvas, {
        type: drawn === "bar" ? "bar" : "line",
        // The hover band marks a bucket's slot, which only the bucketed axis
        // has (see `hoverBandPlugin`).
        plugins:
          xScale === "linear" ? [zoomPlugin] : [zoomPlugin, hoverBandPlugin],
        data: {
          labels: chartLabels(partitionsRef.current, xScale),
          datasets: buildDatasets(
            partitionsRef.current,
            xScale,
            metricNameRef.current,
            canvas,
            hiddenKeysRef.current,
            drawn,
            seriesColor,
          ),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          // Hovering marks the whole BUCKET, every series in it — deliberately,
          // not as a side effect of `index` being the tooltip's mode. The
          // tooltip lists every series' value at that bucket and the click
          // drills into the bucket itself (MetricChart forwards only its
          // bounds), so the highlight names exactly what the pointer is aimed
          // at. It is the single-series rule — the bar at this index lights up
          // — applied to however many series are drawn.
          // The linear axis carries series that do NOT share an index (see
          // `nearestPerGroup`); the category axis does, and `index` is both
          // correct and cheaper there.
          interaction:
            xScale === "linear"
              ? { mode: NEAREST_PER_GROUP, intersect: false }
              : { mode: "index", intersect: false },
          onClick: (
            event: ChartEvent,
            elements: ActiveElement[],
            chart: Chart,
          ) => {
            const hit = clickedElement(event, elements, chart);
            if (hit === undefined) {
              return;
            }
            const datum =
              partitionsRef.current[hit.datasetIndex]?.points[hit.index];
            // A gap-filled placeholder carries no value and stands for no
            // rows, so it is not something to drill into.
            if (datum && datum.value !== undefined) {
              // Everything the chart reported for this click, clicked point
              // first — the same set the tooltip listed, so a caller can drill
              // into exactly the rows the reader was looking at.
              const selection = elements
                .map(
                  (el) =>
                    partitionsRef.current[el.datasetIndex]?.points[el.index],
                )
                .filter(
                  (point): point is PlotDatum =>
                    point !== undefined && point.value !== undefined,
                );
              const ordered = [
                datum,
                ...selection.filter((point) => point !== datum),
              ];
              clickRef.current?.(datum, ordered);
            }
          },
          scales: {
            x:
              xScale === "linear"
                ? {
                    type: "linear",
                    // `undefined` is Chart.js's own "fit to the data", which is
                    // what a chart drawn outside a dashboard still gets.
                    min: xDomainRef.current?.minMs,
                    max: xDomainRef.current?.maxMs,
                    grid: { offset: false },
                    ticks: {
                      autoSkip: true,
                      autoSkipPadding: 24,
                      maxRotation: 0,
                      // Linear ticks land on Chart.js "nice numbers" in epoch
                      // ms rather than on calendar boundaries, so each is
                      // rendered as the date it falls on. (Every dashboard
                      // range is at least 30 days, so date resolution is the
                      // right granularity.) The ×1e6 is ms → ns for the shared
                      // formatter.
                      callback: (value: string | number) =>
                        nsToDateLabel(Number(value) * 1_000_000),
                    },
                  }
                : {
                    // Numeric, positioned by bucket index — see the note above
                    // `PlotDatum` for why this is not a category scale.
                    type: "linear",
                    ...bucketBounds(
                      (chartLabels(partitionsRef.current, xScale) ?? []).length,
                    ),
                    // One tick per BUCKET, stated rather than left to Chart.js.
                    // Its own generator starts at the exact `min` and steps
                    // from there, and `min` is half a bucket below bucket 0
                    // (see `bucketBounds`) — so left alone it produces ticks at
                    // -0.5, 0.5, 1.5 …, every one of them between two buckets
                    // and naming none of them. `autoSkip` still thins these
                    // afterwards, so a long run does not crowd.
                    afterBuildTicks: (axis) => {
                      const ticks = [];
                      for (
                        let bucket = Math.ceil(axis.min);
                        bucket <= Math.floor(axis.max);
                        bucket += 1
                      ) {
                        ticks.push({ value: bucket });
                      }
                      axis.ticks = ticks;
                    },
                    grid: { offset: false },
                    ticks: {
                      autoSkip: true,
                      autoSkipPadding: 24,
                      maxRotation: 0,
                      callback: (value: string | number) =>
                        bucketTickLabel(Number(value), labelsRef.current),
                    },
                  },
            y: {
              beginAtZero: true,
              title: {
                display: Boolean(unitRef.current),
                text: unitRef.current ?? "",
              },
            },
          },
          plugins: {
            // Chart.js's own legend stays off on EVERY path: a canvas legend
            // is laid out inside the plot and cannot scroll, so many series
            // would leave a legend and no chart. The view renders a scrollable
            // HTML one below instead, and only when there is more than one
            // series to tell apart.
            legend: { display: false },
            zoom: {
              // The floor on zooming out is the window the card was drawn for.
              // Past it there is nothing to see: the dashboard fetched this
              // range and no other, so a wider axis would only add blank
              // margins that no other card on the page shows. Re-read from the
              // scale's own options whenever those change, which is how a
              // re-scoped dashboard moves the floor with it.
              limits: { x: { min: "original", max: "original" } },
              pan: {
                // Plain left-drag, no modifier — the plot panel's gesture
                // (`usePan`). A press that never leaves the spot it started in
                // stays a click: the plugin only treats a drag as a pan past
                // its threshold, and swallows the trailing click only when one
                // happened, so the drill-down below is untouched.
                enabled: true,
                mode: "x",
                // Says the plot is grabbable at the moment it is grabbed, the
                // way the plot panel does. Set on the canvas rather than in
                // CSS because `:active` would flash it on a plain click too,
                // and a click here means drill down, not drag.
                onPanStart: ({ chart }) => {
                  chart.canvas.style.cursor = "grabbing";
                  setTooltipEnabled(chart, false);
                  // `undefined` lets the pan proceed; `false` would reject it.
                  return undefined;
                },
                onPanComplete: ({ chart }) => {
                  chart.canvas.style.cursor = "";
                  setTooltipEnabled(chart, true);
                },
                onPan: ({ chart }) => {
                  setZoomed(isXZoomed(chart));
                },
              },
              zoom: {
                // Plain wheel, no modifier, scaled about the cursor — again
                // the plot panel's gesture (`useZoom`), at the same 10% a
                // notch. `mode: "x"` plus the plugin's focal point means the
                // moment under the pointer stays put while the window closes
                // in on it, so a reader zooms into the time they are pointing
                // at rather than into the middle of the card.
                //
                // The cost is that the plugin cancels every wheel it acts on,
                // so a wheel over a card does not scroll the dashboard under
                // it. Deliberate: consistency with the plot panel wins, and
                // there is page either side of the cards to scroll on.
                wheel: { enabled: true, speed: 0.1 },
                pinch: { enabled: true },
                mode: "x",
                onZoomStart: ({ chart }) => {
                  setTooltipEnabled(chart, false);
                  // `undefined` lets the zoom proceed; `false` would reject it.
                  return undefined;
                },
                // Debounced by the plugin (250ms) on the wheel path, which is
                // what carries the suppression across a burst of notches
                // rather than flickering the tooltip between each one.
                onZoomComplete: ({ chart }) => {
                  setTooltipEnabled(chart, true);
                },
                onZoom: ({ chart }) => {
                  setZoomed(isXZoomed(chart));
                },
              },
            },
            tooltip: {
              callbacks: {
                title: (items) => {
                  const first = items[0];
                  if (first === undefined) {
                    return "";
                  }
                  // Read off the item's OWN partition, not partition 0. On the
                  // category axis the two agree — every partition is gap-filled
                  // over the same buckets — but on the linear axis they do not:
                  // each group's points are its own sessions at its own
                  // moments, so partition 0's `index`-th point names a
                  // different instant, and past its length names none at all.
                  return (
                    partitionsRef.current[first.datasetIndex]?.points[
                      first.dataIndex
                    ]?.tooltipTitle ?? ""
                  );
                },
                label: (item) => {
                  const y = item.parsed.y;
                  const value =
                    typeof y !== "number" || Number.isNaN(y)
                      ? "—"
                      : `${String(y)}${unitRef.current ? ` ${unitRef.current}` : ""}`;
                  // With one line the dataset label is the metric name, which
                  // the card's title already says. With several it is the
                  // group, which is the only thing distinguishing the rows.
                  return partitionsRef.current.length > 1
                    ? `${item.dataset.label ?? ""}: ${value}`
                    : value;
                },
              },
            },
          },
        },
      });
      // The window the fresh chart is on, so the first run of the update
      // effect below sees no change and leaves it — and its zoom, once there
      // is one — alone.
      xExtentRef.current = xExtentKey(
        xScale,
        xDomainRef.current,
        partitionsRef.current,
      );
      setZoomed(false);
    },
    [display, xScale, seriesColor],
  );

  // Push series / metric-name / unit changes into the existing chart rather than
  // rebuilding it (Chart.js's recommended path for data updates). The tooltip
  // callback reads the unit off its ref, but the y-axis title is baked into the
  // options object at build time, so it has to be reassigned here.
  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }
    chart.data.labels = chartLabels(partitions, xScale);
    // Rebuilt wholesale rather than patched in place: grouping changes the
    // NUMBER of datasets, not just their contents, and a stale extra dataset
    // would keep drawing a series the response no longer has.
    chart.data.datasets = buildDatasets(
      partitions,
      xScale,
      metricName,
      chart.canvas,
      hiddenKeys,
      drawableDisplay(display, xScale),
      seriesColor,
    );
    // Chart.js keeps visibility on the dataset's METADATA, which survives the
    // wholesale dataset replacement above — so a dataset rebuilt as visible
    // would still be drawn hidden (and vice versa) from whatever the metadata
    // at that index last held. Restating it here is what makes the legend and
    // the plot agree after a refetch or a regrouping.
    partitions.forEach((partition, i) => {
      chart.setDatasetVisibility(i, !hiddenKeys.has(partition.key));
    });
    // `scales.y` is typed as a union over every scale kind (linear, time,
    // radial…), so `title` isn't reachable without narrowing to the cartesian
    // shape we actually build above.
    const yScale = chart.options.scales?.y as
      { title?: { display?: boolean; text?: string } } | undefined;
    if (yScale?.title) {
      yScale.title.display = Boolean(unit);
      yScale.title.text = unit ?? "";
    }
    // Has the WINDOW moved, or only the data inside it? Both arrive here as
    // "props changed", and the answer decides what happens to a zoom.
    const extent = xExtentKey(xScale, xDomain, partitions);
    const windowMoved = extent !== xExtentRef.current;
    xExtentRef.current = extent;

    if (windowMoved) {
      // Out of any zoom first: one into the old window names a span this chart
      // no longer draws. Doing it BEFORE the bounds below rather than after is
      // what makes the new window the one the reader can zoom back out to —
      // `resetZoom` restores the bounds it recorded when the chart last
      // settled, and forgets them, so whatever is written next becomes the
      // whole window as far as the plugin's zoom-out limit is concerned.
      chart.resetZoom("none");
      setZoomed(false);
    }

    // The x-axis's bounds are baked into the options object at build time like
    // the y-axis title, so a window that moves — the dashboard's range
    // changed, or the bucket run did, but neither `display` nor `xScale` did —
    // has to be reassigned here or the axis keeps the span it was created
    // with. (`scales.x` is typed as a union over every scale kind, so it needs
    // the same narrowing the y-axis title did.)
    //
    // Only when the window actually moved, though. These are the same options
    // the zoom plugin writes the reader's zoom into, so restating them on
    // every data change would wipe that zoom out each time a page landed.
    if (windowMoved) {
      const xScaleOptions = chart.options.scales?.x as
        { min?: number; max?: number } | undefined;
      if (xScaleOptions) {
        // Both axes are numeric; they differ only in what the numbers mean —
        // epoch ms on the time axis, bucket indices on the bucketed one.
        const bounds =
          xScale === "linear"
            ? { min: xDomain?.minMs, max: xDomain?.maxMs }
            : bucketBounds(chartLabels(partitions, xScale)?.length ?? 0);
        xScaleOptions.min = bounds.min;
        xScaleOptions.max = bounds.max;
      }
    }

    chart.update();
    // `partitions` is derived from `series` on every render, so `series` is
    // the honest dependency — depending on the derived array would re-run this
    // effect on every render instead.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [
    series,
    metricName,
    display,
    xScale,
    unit,
    xDomain?.minMs,
    xDomain?.maxMs,
    hiddenKeys,
  ]);

  if (series.length === 0) {
    return (
      <p className={styles.empty}>No data points in the selected window.</p>
    );
  }

  const legendVars = seriesColorVars(partitions.length, seriesColor);

  return (
    <div className={styles.chart}>
      {(partitions.length > 1 || groupTruncation) && (
        <div className={styles.legend}>
          {/* The field is not printed here: the card states it on its caption
           * line, and saying it twice in one card only costs the legend room
           * it needs for swatches. The list's accessible name still carries
           * it, which is where a screen reader meets the group values. */}
          <ul
            className={styles.legendItems}
            aria-label={
              groupBy === undefined
                ? `${metricName} series`
                : `${metricName} series by ${groupBy}`
            }
          >
            {partitions.map((partition, i) => {
              const hidden = hiddenKeys.has(partition.key);
              return (
                <li
                  key={partition.key ?? "__no_value__"}
                  className={styles.legendItem}
                >
                  {/* A toggle button rather than a plain row: this is the only
                   * control that switches a series off, so it has to be
                   * reachable by keyboard and to say which state it is in.
                   * `aria-pressed` carries that — the swatch is decorative, and
                   * a colour tells a screen reader nothing. */}
                  <button
                    type="button"
                    className={classNames(styles.legendToggle, {
                      [styles.isHidden]: hidden,
                    })}
                    aria-pressed={!hidden}
                    onClick={() => {
                      toggleSeries(partition.key);
                    }}
                  >
                    <span
                      className={styles.legendSwatch}
                      // Per-series, so inline; still the SAME token the canvas
                      // reads for this series' line.
                      style={{ backgroundColor: `var(${legendVars[i]})` }}
                      aria-hidden="true"
                    />
                    <span className={styles.legendLabel}>
                      {partition.label ?? metricName}
                    </span>
                  </button>
                </li>
              );
            })}
            {/* Not a toggle: there is no series to switch. The explanation
             * rides on `title`, which is also the item's accessible
             * description. */}
            {groupTruncation && (
              <li className={styles.legendMore} title={GROUP_CAP_EXPLANATION}>
                {hiddenGroupsText(groupTruncation.hiddenGroups)}
              </li>
            )}
          </ul>
        </div>
      )}
      <div
        className={classNames(styles.container, {
          [styles.clickable]: Boolean(onDatumClick),
        })}
      >
        <canvas ref={setCanvas} role="img" aria-label={`${metricName} chart`} />
        {/* Named and dressed as the plot panel's control, because it does the
         * same thing to the same kind of view. It appears whenever the reader
         * is not looking at the whole window — the plot panel's rule too — but
         * unlike that one it then stays up rather than fading with the pointer.
         * A full viewport can hide the way out and still look obviously moved;
         * a card this size cannot, so the control doubles as the only standing
         * sign that what is on screen is not the whole window. */}
        {zoomed && (
          <Button
            className={styles.resetView}
            variant="outlined"
            size="small"
            onClick={resetView}
          >
            Reset View
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Which (dataset, point) the user actually clicked.
 *
 * The chart's interaction mode is `index`, so `elements` holds one entry per
 * dataset at the hovered index — and `elements[0]` is always dataset 0, which
 * on a grouped chart would drill into the first series wherever the user
 * clicked. With several series the click is re-resolved in `nearest` mode,
 * which returns the single closest element and therefore the series actually
 * aimed at. With one series there is nothing to disambiguate, so the cheaper
 * (and long-standing) path is kept.
 */
/**
 * The plotted point the click landed on.
 *
 * `elements` is not it: the chart's `interaction.mode` is `"index"`, so
 * Chart.js reports the whole index and hands back dataset 0 first whatever was
 * clicked — which would report the first line's datum for a click on the
 * second. Asking again in `"nearest"` mode finds the series actually under the
 * cursor.
 */
function clickedElement(
  event: ChartEvent,
  elements: ActiveElement[],
  chart: Chart,
): ActiveElement | undefined {
  if (chart.data.datasets.length <= 1 || !event.native) {
    return elements[0];
  }
  return chart.getElementsAtEventForMode(
    event.native,
    "nearest",
    { intersect: false },
    false,
  )[0];
}
